import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";

export const ONBOARDING_DEMO_MILESTONE_EVENT = "onboarding_speaking_demo_milestone_reached";

export const ONBOARDING_DEMO_MILESTONES = [
  "cta_clicked",
  "microphone_ready",
  "microphone_failed",
  "connected",
  "first_tutor_audio",
  "first_learner_speech",
  "first_exchange_completed",
  "offer_rendered",
] as const;

export type OnboardingDemoMilestone = (typeof ONBOARDING_DEMO_MILESTONES)[number];

export type OnboardingConversationMilestones = {
  firstLearnerSpeech: boolean;
  firstExchangeCompleted: boolean;
};

type OnboardingDemoAttempt = {
  attemptId: string;
  recordedMilestones: OnboardingDemoMilestone[];
  startedAt: number;
  startSource: "cta" | "recovered";
  version: 1;
};

type TranscriptMessage = {
  final?: boolean;
  firstReceivedTime?: number;
  id?: string;
  isCompleted?: boolean;
  isFinal?: boolean;
  role?: string;
  source?: string;
  text?: string;
};

type TrackMilestoneOptions = {
  beginNewAttempt?: boolean;
  extraProperties?: Record<string, unknown>;
  now?: number;
};

const STORAGE_KEY = "chickytutor_onboarding_demo_attempt_v1";
const ATTEMPT_MAX_AGE_MS = 60 * 60 * 1000;

const MILESTONE_RANK = Object.fromEntries(
  ONBOARDING_DEMO_MILESTONES.map((milestone, index) => [milestone, index + 1])
) as Record<OnboardingDemoMilestone, number>;

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function createAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `attempt_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function isMilestone(value: unknown): value is OnboardingDemoMilestone {
  return ONBOARDING_DEMO_MILESTONES.includes(value as OnboardingDemoMilestone);
}

function readAttempt(now: number): OnboardingDemoAttempt | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<OnboardingDemoAttempt>;
    if (
      parsed.version !== 1 ||
      typeof parsed.attemptId !== "string" ||
      !parsed.attemptId ||
      typeof parsed.startedAt !== "number" ||
      parsed.startedAt > now ||
      now - parsed.startedAt >= ATTEMPT_MAX_AGE_MS ||
      (parsed.startSource !== "cta" && parsed.startSource !== "recovered")
    ) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      attemptId: parsed.attemptId,
      recordedMilestones: Array.isArray(parsed.recordedMilestones)
        ? parsed.recordedMilestones.filter(isMilestone)
        : [],
      startedAt: parsed.startedAt,
      startSource: parsed.startSource,
      version: 1,
    };
  } catch {
    return null;
  }
}

function writeAttempt(attempt: OnboardingDemoAttempt): void {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // Analytics persistence must never block the speaking experience.
  }
}

function createAttempt(now: number, startSource: OnboardingDemoAttempt["startSource"]) {
  const attempt: OnboardingDemoAttempt = {
    attemptId: createAttemptId(),
    recordedMilestones: [],
    startedAt: now,
    startSource,
    version: 1,
  };
  writeAttempt(attempt);
  return attempt;
}

function claimMilestone(
  milestone: OnboardingDemoMilestone,
  options: Pick<TrackMilestoneOptions, "beginNewAttempt" | "now"> = {}
): Record<string, unknown> | null {
  const now = options.now ?? Date.now();
  const attempt = options.beginNewAttempt
    ? createAttempt(now, "cta")
    : (readAttempt(now) ?? createAttempt(now, "recovered"));

  if (attempt.recordedMilestones.includes(milestone)) return null;

  attempt.recordedMilestones = [...attempt.recordedMilestones, milestone];
  writeAttempt(attempt);

  return {
    onboarding_demo_attempt_id: attempt.attemptId,
    onboarding_demo_attempt_start_source: attempt.startSource,
    milestone,
    milestone_rank: MILESTONE_RANK[milestone],
    attempt_elapsed_ms: Math.max(0, now - attempt.startedAt),
  };
}

export function trackOnboardingDemoMilestone(
  posthog: unknown,
  milestone: OnboardingDemoMilestone,
  context: Record<string, unknown>,
  options: TrackMilestoneOptions = {}
): boolean {
  const milestoneProperties = claimMilestone(milestone, options);
  if (!milestoneProperties) return false;

  trackEventWithAttribution(posthog, ONBOARDING_DEMO_MILESTONE_EVENT, {
    ...context,
    ...milestoneProperties,
    ...options.extraProperties,
  });
  return true;
}

function isCompletedLearnerMessage(message: TranscriptMessage): boolean {
  if (message.role !== "user" || !message.text?.trim()) return false;
  if (message.id?.startsWith("active_user_")) return false;

  return (
    message.isCompleted === true ||
    message.isFinal === true ||
    message.final === true ||
    message.source === "text-stream"
  );
}

export function getOnboardingConversationMilestones(
  messages: TranscriptMessage[]
): OnboardingConversationMilestones {
  const learnerSpeech = messages.some(
    (message) => message.role === "user" && Boolean(message.text?.trim())
  );
  const completedLearnerIndex = messages.findIndex(isCompletedLearnerMessage);
  const completedLearnerTime =
    completedLearnerIndex >= 0 ? messages[completedLearnerIndex].firstReceivedTime : undefined;
  const assistantRespondedAfterLearner = messages.some((message, index) => {
    if (message.role !== "assistant" || !message.text?.trim()) return false;
    if (completedLearnerIndex < 0) return false;

    if (typeof completedLearnerTime === "number" && typeof message.firstReceivedTime === "number") {
      return message.firstReceivedTime > completedLearnerTime;
    }

    return index > completedLearnerIndex;
  });

  return {
    firstLearnerSpeech: learnerSpeech,
    firstExchangeCompleted: completedLearnerIndex >= 0 && assistantRespondedAfterLearner,
  };
}

export function getOnboardingMicrophoneFailureCategory(error: string): string {
  return /permission|notallowed|denied|device/i.test(error)
    ? "permission_or_device"
    : "microphone_or_audio";
}
